import React from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Button, Flex, Heading, Icon, Stack, Text } from '@chakra-ui/react';
import { FiCalendar, FiCheck, FiClock, FiEdit3, FiHome, FiMail, FiPlus } from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import { Tooltip } from '../components/Tip';
import { CalSectionHeading, CalSurface, FadeIn, alpha, useCalTheme } from '../components/ui';
import { BookingShell, BookingSteps, DetailRow } from './BookingParts';

// ==================== TYPES ====================

interface LocationState {
  booking?: {
    id: string;
    title: string;
    startTime: string;
    endTime: string;
  };
  bookingToken?: string;
}

// ==================== COMPONENT ====================

export const PublicBookingSuccessPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useCalTheme();

  const state = location.state as LocationState;

  // ==================== RENDER ====================

  return (
    <BookingShell maxW="container.md">
      <Stack spacing={{ base: 4, md: 6 }}>
        <BookingSteps done={4} />

        {/* The one celebration — shown only after the server returned the booking. */}
        <FadeIn>
          <Stack spacing={3} align="center" textAlign="center" py={{ base: 2, md: 4 }}>
            <Flex w={20} h={20} borderRadius="full" align="center" justify="center" bgImage={t.successGradient}
              boxShadow={`0 12px 36px ${alpha(t.success, 0.35)}`}>
              <Icon as={FiCheck} boxSize={10} color="white" />
            </Flex>
            <Heading as="h1" size={{ base: 'lg', md: 'xl' }} color={t.text} letterSpacing="-0.02em">
              You're booked
            </Heading>
            <Text fontSize="lg" color={t.muted} maxW="46ch">
              Your appointment is confirmed.
            </Text>
          </Stack>
        </FadeIn>

        {state?.booking && (
          <CalSurface>
            <CalSectionHeading icon={FiCalendar} tone="success" title="Appointment details" />
            <Stack spacing={3}>
              {state.booking.title && <DetailRow icon={FiCheck} label="What">{state.booking.title}</DetailRow>}
              <DetailRow icon={FiCalendar} label="Date">{format(parseISO(state.booking.startTime), 'EEEE d MMMM yyyy')}</DetailRow>
              <DetailRow icon={FiClock} label="Time">
                {format(parseISO(state.booking.startTime), 'h:mm a')} – {format(parseISO(state.booking.endTime), 'h:mm a')}
              </DetailRow>
            </Stack>
          </CalSurface>
        )}

        <CalSurface borderColor={alpha(t.success, 0.35)} bg={alpha(t.success, 0.06)}>
          <CalSectionHeading icon={FiMail} tone="success" title="What happens next" />
          <Stack as="ul" spacing={2} pl={5} fontSize="md" color={t.text}>
            <li>Look out for a confirmation email at the address you gave. It may take a few minutes — check your junk folder if it hasn't arrived.</li>
            <li>Add the appointment to your own calendar so you don't miss it.</li>
            <li>Need to change plans? Use the link in your confirmation email{state?.bookingToken ? ', or the Manage my booking button below' : ''}.</li>
          </Stack>
        </CalSurface>

        <Stack spacing={3}>
          {state?.bookingToken && (
            <Tooltip label="See this booking, or cancel it if you need to. Opens your own booking page.">
              <Button size="lg" minH="52px" w="full" variant="outline" leftIcon={<Icon as={FiEdit3} />}
                onClick={() => navigate(`/calendars/booking/manage/${state.bookingToken}`)}>
                Manage my booking
              </Button>
            </Tooltip>
          )}
          <Tooltip label="Go back to the booking page to book another time. Nothing is booked until you confirm again.">
            <Button size="lg" minH="52px" w="full" bgImage={t.accentGradient} color="white" _hover={{ opacity: 0.92 }}
              leftIcon={<Icon as={FiPlus} />} onClick={() => navigate(`/book/${slug}`)}>
              Book another appointment
            </Button>
          </Tooltip>
          <Tooltip label="Go to this site's home page.">
            <Button size="lg" minH="48px" w="full" variant="ghost" color={t.muted} leftIcon={<Icon as={FiHome} />} onClick={() => navigate('/')}>
              Return to home
            </Button>
          </Tooltip>
        </Stack>
      </Stack>
    </BookingShell>
  );
};

export default PublicBookingSuccessPage;
